import {
  IndirectStaffingFunction,
  FlattenedRule,
  ChangeAction,
  IndirectStaffingChange,
  IndirectStaffingRule,
  ModalFormData,
  ValidationResult,
} from "./types";

/**
 * Transforms the nested API response into flat rows for the table.
 * Each function may have multiple rules in ruleList — each becomes its own row.
 */
export function flattenRules(
  staffingFunctions: IndirectStaffingFunction[]
): FlattenedRule[] {
  const rows: FlattenedRule[] = [];

  for (const fn of staffingFunctions) {
    for (const rule of fn.ruleList) {
      rows.push({
        department: fn.department,
        processPath: fn.processPath,
        function: fn.function,
        directRoleMapping: fn.directRoleMapping,
        description: fn.description,
        priority: rule.priority,
        ruleType: rule.ruleType,
        ruleAppliesOn: rule.ruleAppliesOn,
        directBaseRatio: rule.directBaseRatio,
        indirectRequirementRatio: rule.indirectRequirementRatio,
        lever: rule.lever,
      });
    }
  }

  return rows;
}

/** Narrower type for DELETE operations — only the key fields are needed */
export type DeleteFormData = Pick<ModalFormData, "department" | "processPath" | "function">;

/**
 * Constructs the change object for the UpdateIndirectStaffingConfig API
 * based on the modal form state and the action type.
 */
export function buildChangePayload(
  action: "DELETE",
  formData: DeleteFormData,
  ruleType: "FOR_EVERY" | "ALWAYS_HAVE"
): IndirectStaffingChange;
export function buildChangePayload(
  action: "CREATE" | "UPDATE",
  formData: ModalFormData,
  ruleType: "FOR_EVERY" | "ALWAYS_HAVE"
): IndirectStaffingChange;
export function buildChangePayload(
  action: ChangeAction,
  formData: ModalFormData | DeleteFormData,
  ruleType: "FOR_EVERY" | "ALWAYS_HAVE"
): IndirectStaffingChange {
  if (action === "DELETE") {
    return {
      action: "DELETE",
      department: formData.department,
      processPath: formData.processPath,
      function: formData.function,
    };
  }

  const fullFormData = formData as ModalFormData;
  const rule: IndirectStaffingRule = {
    priority: 1,
    ruleType,
    ruleAppliesOn: ruleType === "ALWAYS_HAVE" ? "N/A" : fullFormData.ruleAppliesOn,
    directBaseRatio: ruleType === "FOR_EVERY" ? fullFormData.directBaseRatio : null,
    indirectRequirementRatio: fullFormData.indirectRequirementRatio,
    lever: null,
  };

  return {
    action,
    department: fullFormData.department,
    processPath: fullFormData.processPath,
    function: fullFormData.function,
    directRoleMapping: fullFormData.directRoleMapping,
    description: fullFormData.description,
    ruleList: [rule],
  };
}

/**
 * Validates modal form data for completeness and correctness before submission.
 */
export function validateModalForm(
  formData: ModalFormData,
  ruleType: "FOR_EVERY" | "ALWAYS_HAVE"
): ValidationResult {
  const errors: Record<string, string> = {};

  const FIELD_LABELS: Record<string, string> = {
    department: "Department",
    processPath: "Process Path",
    function: "Function",
    directRoleMapping: "Direct Role Mapping",
  };

  const requiredFields: (keyof ModalFormData)[] = [
    "department",
    "processPath",
    "function",
    "directRoleMapping",
  ];

  for (const field of requiredFields) {
    if (!formData[field] || String(formData[field]).trim() === "") {
      errors[field] = `${FIELD_LABELS[field] ?? field} is required`;
    }
  }

  if (
    formData.indirectRequirementRatio === null ||
    isNaN(formData.indirectRequirementRatio) ||
    formData.indirectRequirementRatio <= 0
  ) {
    errors.indirectRequirementRatio = "Must be a positive number";
  }

  if (ruleType === "FOR_EVERY") {
    if (
      formData.directBaseRatio === null ||
      isNaN(formData.directBaseRatio) ||
      formData.directBaseRatio <= 0
    ) {
      errors.directBaseRatio = "Must be a positive number";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Validation result for a flow path percentage input.
 */
export interface FlowPathValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Validates a flow path percentage input string.
 * Accepts integer values between 0 and 100 inclusive.
 */
export function validateFlowPathPercentage(value: string): FlowPathValidationResult {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return { isValid: false, error: "Percentage is required" };
  }

  if (!/^-?\d+$/.test(trimmed)) {
    return { isValid: false, error: "Must be a whole number" };
  }

  const parsed = Number(trimmed);

  if (parsed < 0 || parsed > 100) {
    return { isValid: false, error: "Must be between 0 and 100" };
  }

  return { isValid: true, error: null };
}
